import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ClipboardPaste, Scale } from 'lucide-react';
import type {
  CategoriaVista,
  CrearPedidoInput,
  PaqueteVista,
  PedidoDetalle,
  PedidoVista,
  ProductoVista,
  UsuarioVista,
} from '@shared/types';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useCarritoStore } from '@renderer/store/useCarritoStore';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { TipoVentaSelector } from '@renderer/components/pos/TipoVentaSelector';
import {
  QuienYADondeForm,
  type ClienteIdentidad,
  type DatosClienteAvanzado,
} from '@renderer/components/pos/QuienYADondeForm';
import type { EntregaInfo } from '@renderer/components/pos/DondeEntregarForm';
import {
  CostoEnvioForm,
  type CostoEnvioInfo,
  type EnvioInfo,
} from '@renderer/components/pos/CostoEnvioForm';
import { MesaSelector } from '@renderer/components/pos/MesaSelector';
import { etiquetaPaso } from '@renderer/components/pos/pasosDomicilio';
import { MenuGrid } from '@renderer/components/pos/MenuGrid';
import { PegarPedidoModal } from '@renderer/components/pos/PegarPedidoModal';
import { VenderAlPesoModal } from '@renderer/components/pos/VenderAlPesoModal';
import { Carrito } from '@renderer/components/pos/Carrito';
import {
  ModificadorPaqueteProductos,
  type ResultadoPaqueteProductos,
} from '@renderer/components/pos/ModificadorPaqueteProductos';
import { CobroModal } from '@renderer/components/cobro/CobroModal';
import { CobroTransferenciaModal } from '@renderer/components/cobro/CobroTransferenciaModal';
import { TicketPreviewModal } from '@renderer/components/ticket/TicketPreviewModal';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { esWeb } from '@renderer/lib/entorno';
import { imprimirComandaWebSiActiva } from '@renderer/lib/impresionNavegador';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourPos } from '@renderer/tours';
import { CheckCircle2, DollarSign, ShoppingBag } from 'lucide-react';
// Sprint 80 — "¿está agotado?" se decide en un solo lugar para las tres pantallas.
import { estaAgotado, topeParaCarrito } from '@shared/stockProducto';
import { esUnidadContinua } from '@shared/medidas';
import { nuevaClaveIntento } from '@renderer/lib/claveIntento';
import { marcarFlujo } from '@renderer/lib/monitoreoUso';

/**
 * Sprint 66 — `donde` es nueva y va ANTES de `menu`.
 *
 * El flujo de domicilio quedó: tipo → cliente → **donde** → menú → envío (costo) → cobro.
 * Antes la dirección se pedía junto con el costo, después del menú, y ahí se descubría que
 * el domicilio estaba fuera de zona con la comida ya capturada.
 */
type Fase =
  | 'tipo'
  | 'cliente'
  | 'mesa'
  | 'menu'
  | 'envio'
  | 'cobro_transferencia';

export function PosPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sesion = useSessionStore((s) => s.sesion);
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';

  // Sprint 39 — Caja MANDATORIA. Si llegan a /pos sin caja abierta
  // (ej. con bookmark o link directo), redirigir al dashboard, que mostrará
  // la pantalla de apertura del día.
  useEffect(() => {
    (async () => {
      const res = await window.api.corte.abierto();
      if (res.ok && !res.data) {
        await window.alertar({
          titulo: 'La caja está cerrada',
          mensaje: 'Antes de tomar pedidos, abre la caja para iniciar el día.',
          variant: 'warn',
        });
        navigate('/dashboard');
      }
    })();
  }, [navigate]);

  // Tipo predefinido por query (?tipo=comer_aqui|delivery): salta TipoVentaSelector.
  // Mesero NO puede tomar delivery: si llega con tipo=delivery se forza a comer_aqui.
  // Si mesero entra a /pos sin query → entrar directo a 'mesa' (no ven el selector tipo).
  const tipoQuery = params.get('tipo') as 'comer_aqui' | 'delivery' | 'para_llevar' | null;
  const esMesero = sesion?.rol === 'mesero';
  /**
   * Sprint 108 — `para_llevar` entra por la URL desde la sección Mostrador.
   *
   * Para el sistema es `comer_aqui` sin mesa; lo que cambia es el camino: no se elige mesa, se
   * pregunta a nombre de quién, y **se cobra antes de mandar a cocina**, porque el cliente está de
   * pie esperando su bolsa y no sentado en una mesa que no se va a ir.
   */
  const esParaLlevar = tipoQuery === 'para_llevar';
  const tipoInicial: 'comer_aqui' | 'delivery' =
    tipoQuery === 'delivery' && esMesero
      ? 'comer_aqui'
      : tipoQuery === 'comer_aqui' || tipoQuery === 'delivery'
      ? tipoQuery
      : 'comer_aqui';
  const tieneTipoPredefinido =
    tipoQuery === 'comer_aqui' || tipoQuery === 'delivery' || esParaLlevar || esMesero;

  // Pre-selección de mesa por query (?mesa=N): salta MesaSelector y entra directo al menú.
  const mesaQuery = (() => {
    const raw = params.get('mesa');
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
  })();

  /**
   * Sprint 98 — Por dónde salir es por dónde se entró.
   *
   * El flujo a domicilio nace en el tablero `/domicilios` (las tres tarjetas del Sprint 95), pero
   * *Atrás* y *Cancelar* mandaban siempre a `/dashboard`. El operador entraba por una puerta y
   * salía por otra, y el tablero que estaba mirando —con sus columnas y sus pedidos en curso—
   * desaparecía sin que él hubiera pedido irse de ahí.
   */
  const rutaSalida =
    params.get('origen') === 'domicilios'
      ? '/domicilios'
      : params.get('origen') === 'mostrador'
        ? '/mostrador'
        : '/dashboard';

  // Sprint 28 — modo "agregar" (?agregar=<pedidoId>): anexar productos a un
  // pedido de mesa ya abierto. Salta tipo/cliente/mesa y entra directo al menú.
  const agregarQuery = (() => {
    const raw = params.get('agregar');
    const n = raw ? Number(raw) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
  })();
  const modoAgregar = agregarQuery !== null;

  const faseInicial: Fase = (() => {
    if (modoAgregar) return 'menu';
    if (tipoInicial === 'delivery') return 'cliente';
    // Sprint 108 — Mostrador no elige mesa: el cliente está enfrente, no sentado.
    if (esParaLlevar) return 'menu';
    if (mesaQuery !== null) return 'menu';
    return tieneTipoPredefinido ? 'mesa' : 'tipo';
  })();

  const [fase, setFase] = useState<Fase>(faseInicial);
  const [tipo, setTipo] = useState<'comer_aqui' | 'delivery'>(tipoInicial);
  /**
   * Sprint 104 — Venta de mostrador: el cliente pasa a recogerlo.
   *
   * Para el sistema sigue siendo `comer_aqui` sin mesa —así no se cae de ninguna pantalla que ya
   * funciona—, y este flag es lo que permite distinguirlo: no pedirle mesa, y llamarlo por su
   * nombre cuando esté listo.
   */
  const [paraRecoger, setParaRecoger] = useState(esParaLlevar || params.get('recoger') === '1');
  const [cliente, setCliente] = useState<ClienteIdentidad | null>(null);
  /** Sprint 66 — Lo que se capturó en el paso 2 (dirección, GPS, referencias). */
  const [entrega, setEntrega] = useState<EntregaInfo | null>(null);
  const [envio, setEnvio] = useState<EnvioInfo | null>(null);
  // Sprint 35 — prerellenado desde cliente recurrente o pedido pegado de WhatsApp.
  const [envioInicial, setEnvioInicial] = useState<EnvioInfo | null>(null);
  const [mesaId, setMesaId] = useState<number | null>(mesaQuery);

  const [categorias, setCategorias] = useState<CategoriaVista[]>([]);
  const [productos, setProductos] = useState<ProductoVista[]>([]);
  const [paquetes, setPaquetes] = useState<PaqueteVista[]>([]);
  const [paqueteEditando, setPaqueteEditando] = useState<PaqueteVista | null>(null);
  /** Sprint 131 — "Quiero un sirloin de 350 gramos": la venta que no cabe en la lista. */
  const [pesandoAbierto, setPesandoAbierto] = useState(false);
  /** Sprint 90 — El modal que lee el chat del cliente. */
  const [pegarAbierto, setPegarAbierto] = useState(false);
  /** Sprint 161 — ¿Se armó este pedido pegando el chat de WhatsApp? */
  const pegoChatRef = useRef(false);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null);
  /**
   * Sprint 78 — Renglones del carrito que el servidor no puede vender.
   *
   * Se pintan en el propio carrito además de decirse en el aviso: un mensaje que se cierra deja
   * al operador buscando cuál de los ocho renglones era.
   */
  const [problemasCarrito, setProblemasCarrito] = useState<Array<{ indice: number; motivo: string }>>([]);
  const [pedidoCreadoId, setPedidoCreadoId] = useState<number | null>(null);
  const [pedidoCreadoFolio, setPedidoCreadoFolio] = useState<number | null>(null);
  const [pedidoEnCobroTransfer, setPedidoEnCobroTransfer] = useState<PedidoVista | null>(null);
  const [cobroId, setCobroId] = useState<number | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [previewPdfPath, setPreviewPdfPath] = useState<string | null>(null);

  // Sprint 12.1b — mesero que atiende (obligatorio, sin default).
  const [meseroAtiendeId, setMeseroAtiendeId] = useState<number | null>(null);
  const [empleadosAtiende, setEmpleadosAtiende] = useState<UsuarioVista[]>([]);
  // Sprint 12.1d — cantidad de comensales (informativo).
  const [cantidadComensales, setCantidadComensales] = useState<number | null>(null);

  // Sprint 28 — modo agregar: pedido destino + confirmación post-agregado.
  const [pedidoAgregar, setPedidoAgregar] = useState<PedidoDetalle | null>(null);
  const [agregadoOk, setAgregadoOk] = useState<{ folio: number; cantidad: number } | null>(null);

  const carrito = useCarritoStore();
  /*
    Sprint 161 — El flujo "venta": empieza cuando el carrito deja de estar vacío y termina al crear
    el pedido. Un inicio sin fin es una venta que se dejó a medias (el monitoreo las cuenta).
  */
  const flujoVentaRef = useRef(nuevaClaveIntento()?.slice(0, 8) ?? Date.now().toString(16).slice(-8));
  const habiaItemsRef = useRef(false);
  useEffect(() => {
    const hay = carrito.items.length > 0;
    if (hay && !habiaItemsRef.current) marcarFlujo('venta', 'inicio', flujoVentaRef.current);
    habiaItemsRef.current = hay;
  }, [carrito.items.length]);
  const catalogoCargado = useRef(false);

  useEffect(() => {
    return () => {
      // Al desmontar, limpia carrito (el usuario salió del POS)
      carrito.limpiar();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Carga catálogo + empleados (mesero atiende).
   *
   * Sprint 98 — Ya no es sólo en la fase `menu`. Los pasos de domicilio pasaron a ser ventanas
   * **sobre** el POS, así que el menú y el carrito son ahora el fondo de esas ventanas: si el
   * catálogo no estuviera cargado, detrás de "Datos del cliente" se vería una pantalla en blanco.
   *
   * Y hay un beneficio que no se había buscado: quien entra pegando el chat del cliente ve, atrás,
   * **lo que el sistema entendió** mientras teclea el nombre. Antes eso sólo se comprobaba dos
   * pasos después.
   */
  useEffect(() => {
    const FASES_CON_CATALOGO = ['menu', 'cliente', 'envio'];
    if (!FASES_CON_CATALOGO.includes(fase)) {
      // Al salir del flujo se olvida, para que el siguiente pedido lea el stock de nuevo.
      catalogoCargado.current = false;
      return;
    }
    // Una sola carga por pedido: avanzar de paso no vuelve a pedir el catálogo cuatro veces.
    if (catalogoCargado.current) return;
    catalogoCargado.current = true;
    (async () => {
      const [c, p, pq, u] = await Promise.all([
        window.api.categorias.listar(),
        window.api.productos.listar(),
        window.api.paquetes.listar(),
        // Sprint 12.1b — listarTodos incluye empleados sin login (cocineros).
        // Sprint 172 — El mesero no lo pide (no tiene ese permiso y no lo necesita): atiende él.
        sesion?.rol === 'mesero'
          ? Promise.resolve({ ok: false as const, error: '' })
          : window.api.usuarios.listarTodos(),
      ]);
      if (sesion?.rol === 'mesero') setMeseroAtiendeId(sesion.usuarioId);
      if (c.ok) setCategorias(c.data.filter((x) => x.habilitado));
      if (p.ok) setProductos(p.data.filter((x) => x.habilitado));
      if (pq.ok) setPaquetes(pq.data.filter((x) => x.habilitado));
      if (u.ok) {
        // Filtramos: solo mesero o dueño pueden APARECER como "atiende".
        const elegibles = u.data.filter((x) => x.activo && (x.rol === 'mesero' || x.rol === 'dueño'));
        setEmpleadosAtiende(elegibles);
        /**
         * Sprint 111 — Quien tiene la sesión abierta viene preseleccionado.
         *
         * El campo es obligatorio y no traía valor, así que el sistema frenaba el pedido **al
         * final** —con todo capturado y el cliente enfrente— para pedir un dato que casi siempre
         * ya conocía. En el cliente alpha la dueña trabaja sola: ese selector era un toque
         * desperdiciado en el 100% de las ventas, y un muro en el peor momento.
         *
         * Se preselecciona, no se impone: quien captura no siempre es quien atiende (una sola PC
         * en el negocio, varios meseros en el piso), así que el selector sigue ahí y se cambia con
         * un clic. Y sólo si el usuario de la sesión es elegible — un cajero no aparece en la
         * lista y no puede quedar como quien atendió la mesa.
         */
        setMeseroAtiendeId((actual) => {
          if (actual !== null) return actual;
          const yo = elegibles.find((x) => x.id === sesion?.usuarioId);
          return yo ? yo.id : null;
        });
      }
    })();
  }, [fase, sesion?.usuarioId, sesion?.rol]);

  // Sprint 28 — carga el pedido destino para mostrar contexto (folio, mesa).
  useEffect(() => {
    if (!modoAgregar || agregarQuery === null) return;
    (async () => {
      const res = await window.api.pedidos.obtener(agregarQuery);
      if (res.ok && res.data) setPedidoAgregar(res.data);
    })();
  }, [modoAgregar, agregarQuery]);

  const totalProductos = carrito.items.reduce(
    (acc, i) => acc + i.precioUnitario * i.cantidad,
    0,
  );

  // Sprint 47 — stock ajustado al carrito actual: resta lo ya reservado en carrito.
  // Productos/paquetes con stockEfectivo===null no tienen insumo → sin límite, no se tocan.
  const productosConStockDisponible = useMemo(() => {
    return productos.map((p) => {
      // Sprint 62 — sin insumo no hay stock que reservar contra el carrito.
      const stockBase = p.stockEfectivo ?? null;
      if (stockBase === null || stockBase === undefined) return p;
      const enCarrito = carrito.items
        .filter((i) => i.productoId === p.id)
        .reduce((acc, i) => acc + i.cantidad, 0);
      if (enCarrito === 0) return p;
      return { ...p, stockEfectivo: Math.max(0, stockBase - enCarrito) };
    });
  }, [productos, carrito.items]);

  const paquetesConStockDisponible = useMemo(() => {
    return paquetes.map((p) => {
      if (p.stockEfectivo === null || p.stockEfectivo === undefined) return p;
      const enCarrito = carrito.items
        .filter((i) => i.paqueteId === p.id)
        .reduce((acc, i) => acc + i.cantidad, 0);
      if (enCarrito === 0) return p;
      return { ...p, stockEfectivo: Math.max(0, p.stockEfectivo - enCarrito) };
    });
  }, [paquetes, carrito.items]);

  const handleSeleccionTipo = (t: 'comer_aqui' | 'delivery' | 'para_llevar') => {
    /*
      Sprint 104 — "Pasa a recogerlo" entra derecho al menú.

      No hay mesa que elegir ni dirección que capturar: el cliente está enfrente esperando. Pedirle
      cualquiera de las dos cosas sería preguntar por preguntar, que es justo lo que hacía el
      camino viejo de capturarlo como "comer aquí sin mesa".
    */
    if (t === 'para_llevar') {
      setTipo('comer_aqui');
      setParaRecoger(true);
      setMesaId(null);
      setFase('menu');
      return;
    }
    setParaRecoger(false);
    setTipo(t);
    if (t === 'delivery') setFase('cliente');
    else setFase('mesa');
  };

  /**
   * Sprint 98 — Quién y a dónde, en una sola respuesta.
   *
   * Eran dos pasos (`cliente` → `donde`) y ahora son uno. Se conserva lo que el Sprint 66 buscaba
   * —la dirección **antes** del menú, para no capturar la comida y descubrir después que no se
   * llega hasta allá—; lo que se quitó fue la pantalla intermedia, no la garantía.
   */
  const handleQuienYADondeListo = (
    c: ClienteIdentidad,
    entregaCapturada: EntregaInfo,
    extras?: DatosClienteAvanzado,
  ) => {
    setCliente(c);
    setEntrega(entregaCapturada);
    /*
      El paso del costo de envío arranca con la dirección ya confirmada: es lo que se le manda al
      grupo de motomandados para cotizar. `extras` sigue viajando para lo que quiera leer el
      historial del cliente más adelante.
    */
    void extras;
    setEnvioInicial({
      ...entregaCapturada,
      costoEnvio: 0,
      // Sprint 98 — Por omisión el envío va dentro del total: a domicilio se cobra por adelantado.
      envioPagaCliente: false,
    });
    setFase('menu');
  };

  const handleMesaSeleccionada = (id: number) => {
    setMesaId(id || null);
    setFase('menu');
  };

  /**
   * Sprint 78 — Tocar el carrito borra las marcas de problema.
   *
   * Los índices se corren al quitar un renglón: mantener las marcas señalaría al vecino
   * equivocado, que es peor que no señalar nada.
   */
  useEffect(() => {
    setProblemasCarrito([]);
  }, [carrito.items.length]);

  /**
   * Sprint 131 — ¿Este negocio vende algo que se pese?
   *
   * Si no, el botón no sale. Un botón que no sirve para nada es una pregunta que el operador
   * tiene que contestarse cada vez que lo ve.
   */
  const hayAlgoQuePesar = useMemo(
    () =>
      productos.some(
        (p) => p.habilitado && p.insumoId && (p.gramaje ?? 0) > 0 && esUnidadContinua(p.unidadVenta ?? ''),
      ),
    [productos],
  );

  const handleClickProducto = (p: ProductoVista) => {
    // Sprint 14.2 — stockEfectivo refleja stock real (derivado del insumo si aplica).
    // Sprint 47 — p ya viene con stock ajustado al carrito (productosConStockDisponible).
    /**
     * Sprint 80 — La regla vive en `shared/stockProducto.ts`, una sola vez.
     *
     * Aquí decía `stockEfectivo ?? inventarioActual`, y ese `inventarioActual` —muerto desde el
     * Sprint 62— vale 0 en todo producto nuevo. `MenuGrid` ya lo había corregido, así que la
     * tarjeta se dejaba tocar y el clic contestaba **"Sin stock"**: la dueña dio de alta sus
     * bebidas y ninguna se podía vender.
     */
    if (estaAgotado(p)) {
      window.alertar({ titulo: 'Sin stock', mensaje: `Ya no hay más "${p.nombre}" disponible.`, variant: 'warn' });
      return;
    }
    // stockMax = stock original del backend (antes de descuentos de carrito).
    // `null` = sin tope, que NO es lo mismo que cero.
    const stockMax = topeParaCarrito(productos.find((x) => x.id === p.id));
    carrito.agregarProducto({
      productoId: p.id,
      nombre: p.nombre,
      precioUnitario: p.precio,
      stockMax,
    });
  };

  /**
   * Sprint 131 — Una pesada entra al carrito como su propio renglón.
   *
   * `agregarAlPeso` no fusiona: dos pesadas del mismo corte son dos renglones distintos, con su
   * peso y su precio. El precio que se ve aquí es una previsualización — **el que se cobra lo
   * recalcula el servidor** con el precio por kilo del producto.
   */
  const handleAgregarAlPeso = (datos: {
    producto: ProductoVista;
    cantidadBase: number;
    precio: number;
    nombre: string;
  }) => {
    carrito.agregarAlPeso({
      productoId: datos.producto.id,
      nombre: datos.nombre,
      precioUnitario: datos.precio,
      cantidadMedida: datos.cantidadBase,
      stockMax: null,
    });
    setPesandoAbierto(false);
  };

  const handleClickPaquete = (p: PaqueteVista) => {
    setPaqueteEditando(p);
  };

  /**
   * Sprint 90 — Las líneas que salieron de leer el chat entran al carrito como cualquier otra.
   *
   * Se pasan por `handleClickProducto` en vez de escribir el carrito directo, para que **hereden
   * todas las comprobaciones que ya existen**: el tope de stock del Sprint 47, el aviso de agotado
   * y el precio del catálogo. Un atajo que se salta las reglas del camino normal es un atajo que
   * tarde o temprano vende algo que no hay.
   */
  const agregarDesdeChat = (
    lineas: Array<{ productoId: number; nombre: string; cantidad: number }>,
  ) => {
    // Sprint 161 — Que sí se pegó el chat es el dato: así se sabe si el atajo se usa.
    if (lineas.length > 0) pegoChatRef.current = true;
    for (const l of lineas) {
      const producto = productosConStockDisponible.find((x) => x.id === l.productoId);
      if (!producto) continue;
      for (let i = 0; i < l.cantidad; i++) handleClickProducto(producto);
    }
  };

  const handleConfirmarPaquete = (r: ResultadoPaqueteProductos) => {
    // Sprint 47 — stockMax del paquete para limitar el + en carrito.
    const stockMaxPaq = paqueteEditando?.stockEfectivo ?? null;
    carrito.agregarPaquete({
      paqueteId: r.paqueteId,
      nombre: r.nombre,
      precioUnitario: r.precioFinal,
      paqueteProductos: r.paqueteProductos,
      notasEspeciales: r.notasEspeciales,
      stockMax: stockMaxPaq,
    });
  };

  /**
   * Sprint 7.2: para comer_aqui, "Confirmar" envía directo a backend (estado=pendiente_cocina).
   * Para delivery, "Confirmar" abre la fase 'envio' donde se piden dirección + envío.
   *
   * Sprint 12.1b: exige seleccionar mesero antes de confirmar.
   */
  /**
   * Sprint 159 — Un toque, un pedido.
   *
   * En la base real aparecieron diez pedidos guardados dos veces en el mismo segundo. El botón se
   * bloqueaba con `enviando`, pero eso ocurría DESPUÉS de preguntarle al servidor si el carrito se
   * podía vender: un segundo toque en ese viaje de ida y vuelta arrancaba otra captura completa.
   * Un `useRef` cambia en el acto, sin esperar a que React vuelva a pintar el botón.
   *
   * Esto es la cortesía de la pantalla. La garantía la da el servidor con `claveIntento`.
   */
  const confirmandoRef = useRef(false);
  const handleConfirmarCarrito = async () => {
    if (confirmandoRef.current) return;
    confirmandoRef.current = true;
    setEnviando(true);
    try {
      await confirmarCarrito();
    } finally {
      confirmandoRef.current = false;
      setEnviando(false);
    }
  };

  const confirmarCarrito = async () => {
    // Sprint 28 — modo agregar: anexa al pedido de mesa existente. No exige
    // mesero ni comensales (el pedido destino ya los tiene).
    if (modoAgregar) {
      await enviarAgregado();
      return;
    }
    if (!meseroAtiendeId) {
      await window.alertar({
        titulo: 'Falta asignar mesero',
        mensaje: 'Selecciona quién atiende esta orden antes de confirmar.',
        variant: 'warn',
      });
      return;
    }
    /**
     * Sprint 78 — Se pregunta AQUÍ si el carrito se puede vender.
     *
     * Reclamo del negocio, textual: *"¿para qué me dejas hacer todo el proceso si no puedo
     * vender?"*. Había capturado el pedido, la dirección, la cotización del envío y el nombre del
     * repartidor, y el rechazo llegó en el último botón —por un paquete al que le habían retirado
     * un renglón—. Todo ese trabajo perdido, con el cliente esperando.
     *
     * Este es el último momento en que el problema **todavía se puede arreglar** sin tirar nada:
     * el carrito está a la vista y basta quitar el renglón que estorba.
     *
     * Las validaciones son las del servidor —el mismo código— y **no sustituyen** a las de crear:
     * entre validar y cobrar puede agotarse un insumo. Si esto falla por red, se sigue adelante:
     * un preflight que no responde no puede impedir vender.
     */
    const itemsParaValidar = carrito.items.map((i) => ({
      productoId: i.productoId,
      paqueteId: i.paqueteId,
      cantidad: i.cantidad,
      notasEspeciales: i.notasEspeciales,
      paqueteProductos: i.paqueteProductos?.map((s) => ({
        paqueteProductoId: s.paqueteProductoId,
        productoFinalId: s.productoFinalId,
        excluido: s.excluido,
      })),
    }));
    const revision = await window.api.pedidos.validarCarrito({ items: itemsParaValidar });
    if (revision.ok && revision.data.length > 0) {
      setProblemasCarrito(revision.data);
      const detalle = revision.data
        .map((p) => `• ${carrito.items[p.indice]?.nombre ?? 'Un renglón'}: ${p.motivo}`)
        .join('\n');
      await window.alertar({
        titulo:
          revision.data.length === 1
            ? 'Hay un renglón que no se puede vender'
            : `Hay ${revision.data.length} renglones que no se pueden vender`,
        mensaje: `${detalle}\n\nQuítalos del carrito (o vuelve a agregarlos) y sigue. Todavía no se capturó nada más.`,
        variant: 'warn',
      });
      return;
    }
    setProblemasCarrito([]);

    if (tipo === 'delivery') {
      setFase('envio');
    } else {
      await enviarPedido(null);
    }
  };

  /**
   * Sprint 159 — La clave del intento de captura.
   *
   * Es la misma mientras el contenido no cambie: si el primer envío sí llegó pero la respuesta se
   * perdió, repetir trae de vuelta ese pedido en lugar de crear otro. Si la cajera corrige el
   * carrito, es otro intento y lleva otra clave. Se suelta al confirmar que el pedido existe.
   */
  const intentoRef = useRef<{ firma: string; clave: string } | null>(null);
  const claveParaEsteIntento = (input: CrearPedidoInput): string | undefined => {
    const firma = JSON.stringify(input);
    if (intentoRef.current?.firma === firma) return intentoRef.current.clave;
    const clave = nuevaClaveIntento();
    intentoRef.current = clave ? { firma, clave } : null;
    return clave;
  };

  const enviandoPedidoRef = useRef(false);

  /**
   * Crea el pedido en backend.
   * - comer_aqui: backend pone estado='pendiente_cocina', estadoPago='pagado' → cocina inmediato.
   * - delivery: backend pone estado='abierto', estadoPago='pendiente_pago' → falta comprobante.
   */
  /**
   * Sprint 76 — `dejarEsperando` guarda el pedido **sin** cotizar el envío.
   *
   * Es el caso que el flujo real siempre tuvo y el sistema no sabía nombrar: el grupo de
   * motomandados todavía no contesta y hay otro cliente escribiendo. El pedido queda vivo en el
   * tablero de domicilios con su reloj corriendo, en lugar de obligar a cancelarlo o a dejar la
   * pantalla abierta a medias.
   */
  const enviarPedido = async (envioForm: EnvioInfo | null, dejarEsperando = false) => {
    // Sprint 159 — El formulario del envío también se puede tocar dos veces.
    if (enviandoPedidoRef.current) return;
    enviandoPedidoRef.current = true;
    try {
      await enviarPedidoUnaVez(envioForm, dejarEsperando);
    } finally {
      enviandoPedidoRef.current = false;
    }
  };

  const enviarPedidoUnaVez = async (envioForm: EnvioInfo | null, dejarEsperando: boolean) => {
    if (!sesion) return;
    setEnviando(true);
    setErrorEnvio(null);

    if (!meseroAtiendeId) {
      setEnviando(false);
      setErrorEnvio('Selecciona quién atiende esta orden antes de confirmar.');
      return;
    }
    const input: CrearPedidoInput = {
      tipo,
      // Sprint 104 — Venta de mostrador. Sigue siendo `comer_aqui` sin mesa; esto sólo lo nombra.
      paraRecoger,
      usuarioId: sesion.usuarioId,
      meseroAtiendeId,
      cantidadComensales: cantidadComensales && cantidadComensales > 0 ? cantidadComensales : undefined,
      mesaId: mesaId ?? undefined,
      items: carrito.items.map((i) => ({
        productoId: i.productoId,
        paqueteId: i.paqueteId,
        cantidad: i.cantidad,
        // Sprint 131 — Venta al peso: viaja la medida, nunca el precio. El servidor lo calcula.
        cantidadMedida: i.cantidadMedida,
        notasEspeciales: i.notasEspeciales,
        paqueteProductos: i.paqueteProductos?.map((s) => ({
          paqueteProductoId: s.paqueteProductoId,
          productoFinalId: s.productoFinalId,
          excluido: s.excluido,
        })),
      })),
      clienteNombre: cliente?.nombre,
      clienteTelefono: cliente?.telefono,
      // Sprint 66 — Sólo va si se preguntó; `undefined` no da de baja a nadie.
      aceptaPublicidad: cliente?.aceptaPublicidad,
      clienteDireccion: envioForm?.direccion,
      clienteLatitud: envioForm?.latitud,
      clienteLongitud: envioForm?.longitud,
      referenciasEntrega: envioForm?.referenciasEntrega,
      costoEnvio: envioForm?.costoEnvio,
      envioPagaCliente: envioForm?.envioPagaCliente,
      // Sprint 76 — Quién viene por él, si el grupo ya lo dijo.
      nombreRepartidor: envioForm?.nombreRepartidor,
      // Sprint 76 — `false` = se guarda a propósito sin cotizar; el tablero lo va a reclamar.
      envioCotizado: !dejarEsperando,
      /*
        Sprint 137 — El acuerdo de cobrar al entregar. Nunca junto con "dejarlo esperando": sin
        total cotizado no hay monto que cobrar en la puerta, y mandar comida a crédito sin saber
        cuánto vale es la peor de las dos decisiones juntas.
      */
      pagaAlEntregar: dejarEsperando ? false : envioForm?.pagaAlEntregar === true,
      pagaCon: dejarEsperando ? undefined : envioForm?.pagaCon,
      /*
        Sprint 161 — Cómo se capturó el domicilio: el chat pegado manda sobre la tarjeta por la que
        se entró (puede entrar por "WhatsApp" y teclearlo, que es justo lo que hay que saber).
      */
      canalEntrada:
        tipo !== 'delivery'
          ? undefined
          : pegoChatRef.current
            ? 'chat'
            : params.get('canal') === 'chat'
              ? 'chat_tecleado'
              : params.get('canal') === 'telefono'
                ? 'telefono'
                : 'pos',
    };

    /*
      Sprint 159 — Si el servidor contesta `repetido`, es que el primer envío sí llegó y su
      respuesta se perdió: esa vez la pantalla no imprimió ni siguió el camino, así que seguirlo
      ahora es lo correcto. (Dos envíos simultáneos ya no pueden salir de aquí: los frena el ref.)
    */
    const res = await window.api.pedidos.crear({ ...input, claveIntento: claveParaEsteIntento(input) });
    setEnviando(false);
    if (res.ok) {
      intentoRef.current = null;
      pegoChatRef.current = false;
      marcarFlujo('venta', 'fin', flujoVentaRef.current);
      flujoVentaRef.current = nuevaClaveIntento()?.slice(0, 8) ?? Date.now().toString(16).slice(-8);
    }

    if (!res.ok) {
      // Sprint 44 hotfix — antes el error caía en un <span> chiquito al lado
      // del header (línea 538), invisible cuando la dueña está mirando el
      // carrito abajo a la derecha. Usar window.alertar (modal centrado
      // bloqueante) que ya es el patrón del resto del sistema para errores
      // críticos de venta (stock insuficiente, validación, etc).
      const mensaje = traducirErrorBackend(res.error, res.codigo);
      setErrorEnvio(mensaje); // mantiene también el indicador inline como redundancia
      await window.alertar({
        titulo: 'No se pudo enviar el pedido',
        mensaje,
        variant: 'warn',
      });
      return;
    }

    if (tipo === 'delivery' && dejarEsperando) {
      /**
       * Sprint 76 — Queda guardado y esperando la cotización. No se abre el cobro: todavía no
       * hay total que cobrar, porque falta saber cuánto cobra la moto.
       */
      carrito.limpiar();
      navigate('/domicilios');
    } else if (tipo === 'delivery' && envioForm?.pagaAlEntregar) {
      /**
       * Sprint 137 — Contra entrega: no hay comprobante que pedir, hay comida que hacer.
       *
       * Abrir aquí el modal de transferencia sería el defecto entero del sprint en una línea:
       * pedirle al cliente un pago que se acordó cobrar **después**. El pedido se manda a la
       * parrilla y quien atiende vuelve al tablero, que es donde va a seguir su vida.
       *
       * Si el envío a cocina falla, el pedido **no se pierde**: queda en el tablero con su
       * motivo propio ("se cobra al entregar · falta mandarlo") y un botón para reintentarlo.
       * Por eso el error se avisa sin bloquear ni deshacer la captura.
       */
      const aCocina = await window.api.pedidos.enviarACocina(res.data.id);
      carrito.limpiar();
      if (!aCocina.ok) {
        await window.alertar({
          titulo: 'El pedido quedó guardado, pero no salió a cocina',
          mensaje: `${traducirErrorBackend(aCocina.error, aCocina.codigo)}\n\nLo vas a encontrar en el tablero de domicilios para mandarlo desde ahí.`,
          variant: 'warn',
        });
      } else if (esWeb()) {
        void imprimirComandaWebSiActiva(res.data.id);
      }
      navigate('/domicilios');
    } else if (tipo === 'delivery') {
      // No es pedido "creado"; falta comprobante. Saltamos al modal de transferencia.
      setPedidoEnCobroTransfer(res.data);
      setFase('cobro_transferencia');
      carrito.limpiar();
    } else if (paraRecoger) {
      /**
       * Sprint 111 — **Primero se surte, después se cobra.** Lo dijo la dueña.
       *
       * El Sprint 108 abría el cobro solo, apenas se capturaba el pedido. La razón que dimos —el
       * dueño del producto y yo— sonaba sensata: *"el cliente está de pie con la cartera en la
       * mano, cobrarle después es perseguirlo"*. Nunca se lo preguntamos a quien atiende el
       * mostrador. Se le preguntó el 2026-08-26 y contestó cuatro palabras:
       *
       *     "Primero se surte el pedido."
       *
       * No es un matiz: era el argumento entero. Y ya venía debilitado por DT-102 —aquí **todo se
       * vende ya cocinado**, así que surtir es pesar y armar, un minuto— y por lo que ella misma
       * dijo del arrepentimiento: se devuelve el dinero y **el producto no se pierde**. Con eso,
       * cobrar por adelantado no protege de nada y sí estorba: obliga a cobrar antes de saber si
       * de verdad hay todo lo que pidió.
       *
       * El pedido cae en la columna **"Falta cobrar"** del tablero de mostrador, que es donde ella
       * lo va a buscar cuando la bolsa esté lista. La compuerta que sí es suya sigue en pie y la
       * hace cumplir el servidor: **no se marca entregado sin un pago registrado**
       * (`marcarEntregadoMostrador`).
       */
      setPedidoCreadoId(res.data.id);
      setPedidoCreadoFolio(res.data.numeroPedido);
      carrito.limpiar();
      if (esWeb()) void imprimirComandaWebSiActiva(res.data.id);
    } else {
      setPedidoCreadoId(res.data.id);
      setPedidoCreadoFolio(res.data.numeroPedido);
      carrito.limpiar();
      // Sprint 56.1 — El handler ya disparó la comanda por la térmica de
      // cocina (o la de caja). Eso no aplica en web: ahí la manda el navegador.
      if (esWeb()) void imprimirComandaWebSiActiva(res.data.id);
    }
  };

  /**
   * Sprint 28 — Anexa los productos del carrito al pedido de mesa en curso.
   * El backend valida tipo/estado del pedido y recalcula el total.
   */
  const enviarAgregado = async () => {
    if (!sesion || agregarQuery === null || carrito.items.length === 0) return;
    setEnviando(true);
    setErrorEnvio(null);

    const res = await window.api.pedidos.agregarItems({
      pedidoId: agregarQuery,
      usuarioId: sesion.usuarioId,
      items: carrito.items.map((i) => ({
        productoId: i.productoId,
        paqueteId: i.paqueteId,
        cantidad: i.cantidad,
        // Sprint 131 — Venta al peso: viaja la medida, nunca el precio. El servidor lo calcula.
        cantidadMedida: i.cantidadMedida,
        notasEspeciales: i.notasEspeciales,
        paqueteProductos: i.paqueteProductos?.map((s) => ({
          paqueteProductoId: s.paqueteProductoId,
          productoFinalId: s.productoFinalId,
          excluido: s.excluido,
        })),
      })),
    });
    setEnviando(false);

    if (!res.ok) {
      // Sprint 44 hotfix — mismo problema que `enviar()`: el error caía en un
      // span chiquito invisible. Modal centrado bloqueante.
      const mensaje = traducirErrorBackend(res.error, res.codigo);
      setErrorEnvio(mensaje);
      await window.alertar({
        titulo: 'No se pudo agregar al pedido',
        mensaje,
        variant: 'warn',
      });
      return;
    }
    setAgregadoOk({ folio: res.data.pedido.numeroPedido, cantidad: carrito.items.length });
    carrito.limpiar();
    // Sprint 56.2 — comanda de sólo lo agregado (en desktop la disparó el handler).
    if (esWeb()) {
      void imprimirComandaWebSiActiva(res.data.pedido.id, res.data.itemsNuevosIds);
    }
  };

  /**
   * Sprint 66 — El envío completo = la entrega del paso 2 + el costo de este paso.
   *
   * Si no hubiera entrega (no debería: el paso 2 es obligatorio) se manda de vuelta en lugar de
   * crear un pedido de domicilio sin dirección, que el backend rechazaría con un error técnico.
   */
  const handleCostoListo = (costo: CostoEnvioInfo) => {
    if (!entrega) {
      setErrorEnvio('Falta la dirección de entrega.');
      setFase('cliente');
      return;
    }
    const completo: EnvioInfo = { ...entrega, ...costo };
    setEnvio(completo);
    enviarPedido(completo);
  };

  /**
   * Sprint 76 — "Todavía no contestan": se guarda el pedido y se vuelve al tablero.
   *
   * Se guarda lo que haya (puede que ya tengan el nombre del repartidor y falte el precio), con
   * el envío marcado como **no cotizado** para que el tablero sepa qué le falta.
   */
  const handleDejarEsperando = (parcial: CostoEnvioInfo) => {
    if (!entrega) {
      setErrorEnvio('Falta la dirección de entrega.');
      setFase('cliente');
      return;
    }
    enviarPedido({ ...entrega, ...parcial, costoEnvio: 0 }, true);
  };

  const handleTransferenciaConfirmada = () => {
    // El pedido ya está pagado y en cocina. Cerramos modal y vamos a "nuevo pedido".
    setPedidoEnCobroTransfer(null);
    handleNuevoPedido();
  };

  const handleCobrarAhora = () => {
    if (pedidoCreadoId) {
      setCobroId(pedidoCreadoId);
      setPedidoCreadoId(null);
    }
  };

  const handleNuevoPedido = () => {
    setPedidoCreadoId(null);
    setPedidoCreadoFolio(null);
    setCliente(null);
    setEnvio(null);
    setEnvioInicial(null);
    setMesaId(null);
    setFase(tieneTipoPredefinido ? (tipoInicial === 'delivery' ? 'cliente' : 'mesa') : 'tipo');
  };

  const handleCobrado = (pdfPath?: string) => {
    setPreviewPdfPath(pdfPath ?? null);
    setPreviewId(cobroId);
    setCobroId(null);
  };

  const handleCerrarPreview = () => {
    setPreviewId(null);
    setPreviewPdfPath(null);
    setPedidoCreadoFolio(null);
    setCliente(null);
    setEnvio(null);
    setEnvioInicial(null);
    setMesaId(null);
    /**
     * Sprint 108 — El mostrador vuelve a su tablero, no a otra captura en blanco.
     *
     * Cobrado e impreso el ticket, lo siguiente no es teclear otro pedido: es **ver la cola**, que
     * es donde está el cliente al que hay que entregarle. Tomar el siguiente es un toque desde ahí.
     */
    if (paraRecoger) {
      navigate('/mostrador');
      return;
    }
    setFase(tieneTipoPredefinido ? (tipoInicial === 'delivery' ? 'cliente' : 'mesa') : 'tipo');
  };

  const cancelar = async () => {
    if (carrito.items.length > 0) {
      const ok = await window.confirmar({
        titulo: '¿Cancelar pedido?',
        mensaje: 'Se perderán los items agregados al carrito.',
        textoConfirmar: 'Sí, cancelar',
        textoCancelar: 'No, seguir',
        variant: 'warn',
      });
      if (!ok) return;
    }
    carrito.limpiar();
    // Sprint 28 — en modo agregar regresamos a la pantalla de origen (mesas / pedidos).
    if (modoAgregar) navigate(-1);
    else navigate(rutaSalida);
  };

  if (fase === 'tipo') {
    return (
      <div className="h-full">
        <TipoVentaSelector onSelect={handleSeleccionTipo} />
      </div>
    );
  }

  if (fase === 'mesa') {
    return (
      <div className="h-full">
        <MesaSelector
          onSiguiente={handleMesaSeleccionada}
          onAtras={() => (tieneTipoPredefinido ? navigate('/dashboard') : setFase('tipo'))}
          permitirSinMesa
        />
      </div>
    );
  }

  if (fase === 'cobro_transferencia') {
    return (
      <div className="h-full">
        <CobroTransferenciaModal
          abierto={pedidoEnCobroTransfer !== null}
          pedido={pedidoEnCobroTransfer}
          onClose={() => {
            /*
              Sprint 98 — Dejarlo esperando devuelve al tablero, no a un pedido en blanco.

              Cerrar esta ventana abría otra captura vacía, como si lo único que se pudiera hacer
              después de un pedido fuera empezar el siguiente. Pero quien deja un pago esperando
              casi siempre quiere lo contrario: **ver dónde quedó** y seguir con lo que ya tenía en
              curso. El pedido sigue vivo en `pendiente_pago` y aparece en la columna "Esperando
              respuesta" marcado como "Falta el comprobante".
            */
            setPedidoEnCobroTransfer(null);
            carrito.limpiar();
            navigate(rutaSalida);
          }}
          onConfirmado={handleTransferenciaConfirmada}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div
        className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-2"
        data-tour="pos-header"
      >
        <button
          onClick={async () => {
            if (carrito.items.length > 0) {
              const ok = await window.confirmar({
                titulo: 'Vaciar carrito',
                mensaje: 'Si regresas se vacía el carrito. ¿Continuar?',
                textoConfirmar: 'Sí, regresar',
                textoCancelar: 'Seguir',
                variant: 'warn',
              });
              if (!ok) return;
            }
            carrito.limpiar();
            if (modoAgregar) {
              navigate(-1);
            } else if (tipo === 'delivery') {
              // Sprint 98 — Atrás desde el menú es "¿quién y a dónde?", que ya trae la dirección.
              setFase('cliente');
            } else if (tieneTipoPredefinido) {
              navigate(rutaSalida);
            } else {
              setFase('tipo');
            }
          }}
          className="btn-ghost p-2"
          aria-label="Atrás"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="text-sm text-slate-600">
          {modoAgregar
            ? /*
                Sprint 140 — Agregar dejó de ser cosa sólo de mesas, así que el rótulo dice de
                QUÉ pedido se trata en los tres canales: la mesa, el nombre de quien espera en el
                mostrador o el del domicilio. Decir sólo el folio obliga a recordar un número que
                nadie memoriza con el cliente enfrente.
              */
              `➕ Agregando a #${String(pedidoAgregar?.numeroPedido ?? 0).padStart(5, '0')}${
                pedidoAgregar?.mesaId
                  ? ` · Mesa #${pedidoAgregar.mesaId}`
                  : pedidoAgregar?.clienteNombre
                  ? ` · ${pedidoAgregar.clienteNombre}`
                  : pedidoAgregar?.tipo === 'delivery'
                  ? ' · A domicilio'
                  : ' · Mostrador'
              }`
            : paraRecoger
            ? '🛍️ Venta de mostrador'
            : tipo === 'comer_aqui'
            ? `🍽️ Comer aquí${mesaId ? ` · Mesa #${mesaId}` : ''}`
            : /*
                 Sprint 98 — El paso se pregunta, no se afirma. Decía "Paso 3 de 5" fijo, y con las
                 ventanas abiertas encima ese número habría mentido en tres de los cuatro pasos.
               */
              `🏍️ A domicilio${etiquetaPaso(fase) ? ` · ${etiquetaPaso(fase)}` : ''}${
                cliente ? ` · ${cliente.nombre}` : ''
              }`}
        </span>
        {errorEnvio && (
          <span className="text-sm text-red-600" role="alert">
            ⚠️ {errorEnvio}
          </span>
        )}

        {/*
          Sprint 111 — Quién atiende sube al encabezado. Petición del dueño, con flecha.

          Vivía abajo a la derecha, arriba del carrito, y era **obligatorio antes de confirmar**:
          se descubría al final, con el pedido entero capturado y el cliente enfrente. Un dato que
          frena la venta no puede estar donde hay que buscarlo. Arriba se ve desde dos metros y se
          resuelve antes de tocar el primer producto.

          Sigue pintándose en ámbar mientras esté vacío: mover un campo no debe apagar la señal
          de que falta: sólo adelantarla.
        */}
        {/* Sprint 172 — El mesero atiende lo que él captura (el servidor lo impone): no hay nada que escoger. */}
        {!modoAgregar && sesion?.rol === 'mesero' && (
          <span className="ml-3 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700">
            👤 Atiendes tú
          </span>
        )}
        {!modoAgregar && sesion?.rol !== 'mesero' && (
          <label
            className={`ml-3 flex items-center gap-2 rounded-lg border px-2 py-1 text-xs ${
              meseroAtiendeId
                ? 'border-slate-200 bg-slate-50 text-slate-600'
                : 'border-amber-400 bg-amber-50 text-amber-800'
            }`}
          >
            <span className="whitespace-nowrap font-medium">
              {meseroAtiendeId ? '👤 Atiende' : '👤 ¿Quién atiende?'}
            </span>
            <select
              id="meseroAtiende"
              className="min-h-[36px] rounded border-0 bg-transparent text-xs font-semibold focus:ring-1 focus:ring-primary"
              value={meseroAtiendeId ?? ''}
              onChange={(e) => setMeseroAtiendeId(e.target.value ? Number(e.target.value) : null)}
              disabled={enviando}
            >
              <option value="">— Selecciona —</option>
              {empleadosAtiende.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre} ({u.rol === 'dueño' ? 'Dueño/a' : 'Mesero'})
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="ml-auto flex items-center gap-2">
          {/*
            Sprint 90 — El atajo que ataca el trabajo real: 9 de cada 10 ventas nacen en un mensaje
            de WhatsApp que hoy se teclea a mano, 50 a 150 veces al día. Va visible en la barra y no
            escondido en un menú: un atajo que no se ve desde dos metros no lo usa nadie.
          */}
          {tipo === 'delivery' && (
            <button
              onClick={() => setPegarAbierto(true)}
              data-uso="venta:pegar-whatsapp"
              className="btn-ghost min-h-[40px] border border-slate-300 text-sm"
              title="Pega el mensaje del cliente y lo convierto en pedido"
            >
              <ClipboardPaste className="h-4 w-4" />
              Pegar de WhatsApp
            </button>
          )}
          {/*
            Sprint 131 — La venta que no cabe en la lista.

            Va en la barra y no en cada tarjeta del menú: es el caso raro, y meter un segundo botón
            en las tarjetas habría hecho más lenta la venta normal —la de cien veces al día— para
            servir a la excepción. Sólo aparece si hay algo que se pese: un botón que no sirve para
            nada es una pregunta que el operador tiene que contestarse cada vez.
          */}
          {hayAlgoQuePesar && (
            <button
              onClick={() => setPesandoAbierto(true)}
              className="btn-ghost min-h-[40px] border border-slate-300 text-sm"
              title="El cliente pide una cantidad que no está en tu lista"
            >
              <Scale className="h-4 w-4" />
              Vender al peso
            </button>
          )}
          <HelpButton tour={tourPos} label="Ayuda" />
        </div>
      </div>

      <VenderAlPesoModal
        abierto={pesandoAbierto}
        productos={productosConStockDisponible}
        monedaSimbolo={monedaSimbolo}
        locale={negocio?.locale ?? 'es-MX'}
        onCerrar={() => setPesandoAbierto(false)}
        onAgregar={handleAgregarAlPeso}
      />

      <PegarPedidoModal
        abierto={pegarAbierto}
        productos={productosConStockDisponible}
        monedaSimbolo={monedaSimbolo}
        onCerrar={() => setPegarAbierto(false)}
        onAgregar={agregarDesdeChat}
      />

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-hidden" data-tour="pos-menu">
          <MenuGrid
            productos={productosConStockDisponible}
            paquetes={paquetesConStockDisponible}
            categorias={categorias}
            onClickProducto={handleClickProducto}
            onClickPaquete={handleClickPaquete}
          />
        </div>

        <div className="flex w-80 shrink-0 flex-col lg:w-96">
          {/* Sprint 12.1b — Selector obligatorio de mesero que atiende.
              Sprint 28 — oculto en modo agregar (el pedido destino ya lo tiene). */}
          {/*
            Sprint 108 — "¿A nombre de quién?", el primer dato del mostrador.

            Es lo que se grita cuando la bolsa está lista, y por eso va **antes** que nada en la
            columna del carrito. Sin nombre hay que llamar por número de folio, y nadie levanta la
            vista cuando gritan "cero cero cero veintiocho".

            No es obligatorio: si el cliente no lo quiere dar, el pedido se toma igual. Bloquear una
            venta por un dato de cortesía sería exactamente lo que este proyecto no hace.
          */}
          {paraRecoger && !modoAgregar && (
            <div className="border-b border-l border-teal-300 bg-teal-50 px-4 py-3">
              <label htmlFor="nombreMostrador" className="mb-1 block text-xs font-medium text-teal-900">
                🛍️ ¿A nombre de quién?
              </label>
              <input
                id="nombreMostrador"
                className="input w-full text-sm"
                value={cliente?.nombre ?? ''}
                onChange={(e) => setCliente({ nombre: e.target.value })}
                placeholder="Juan, la señora de rojo…"
                autoFocus
                maxLength={60}
              />
              <p className="mt-1 text-xs text-teal-800">
                Sale en su ticket y es como lo llamas cuando esté listo.
              </p>
            </div>
          )}

          {/*
            Sprint 111 — Aquí vivía "Mesero que atiende". Se mudó al encabezado (arriba en este
            mismo archivo): era obligatorio y se descubría al final de la captura. No se duplica:
            dos controles para el mismo dato es cómo se aprende que uno de los dos miente.
          */}
          {/*
            Sprint 12.1d — Comensales (solo informativo, opcional).

            Sprint 111 — Fuera del mostrador, y la caja ya no se pinta vacía. En Venta de mostrador
            **no hay mesa**: el cliente está de pie y se lleva su bolsa. La pregunta venía de copiar
            la pantalla del salón y en el piso se lee como un formulario que no aplica — el operador
            se detiene a decidir si debe contestarla. Reportado por el dueño con una tacha encima.
          */}
          {!modoAgregar && tipo === 'comer_aqui' && !paraRecoger && (
          <div className="px-4 py-3 border-l border-b bg-slate-50 border-slate-200">
              <div>
                <label htmlFor="comensales" className="block text-xs font-medium text-slate-700 mb-1">
                  👥 ¿Cuántas personas en la mesa? <span className="text-slate-400">(opcional)</span>
                </label>
                <input
                  id="comensales"
                  type="number"
                  min={1}
                  max={50}
                  className="input w-full text-sm text-center font-mono"
                  placeholder="Ej. 4"
                  /* Sprint 30 — select al foco + blur al scroll: misma defensa
                     que NumberField (evita concatenar dígitos / cambio con rueda). */
                  onFocus={(e) => e.currentTarget.select()}
                  onWheel={(e) => e.currentTarget.blur()}
                  value={cantidadComensales ?? ''}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setCantidadComensales(Number.isFinite(v) && v > 0 ? v : null);
                  }}
                  disabled={enviando}
                />
              </div>

              {/*
                Sprint 127 — "¿La mesa a nombre de quién?".

                Petición del dueño, con su propia analogía: si en el mostrador se pregunta el
                nombre, en la mesa es la misma pregunta de toda la vida. Sirve para dos cosas
                concretas: llamar a la mesa correcta cuando sale la comanda, y que el ticket diga
                a quién se le cobró.

                **Opcional y sin `autoFocus`**, a diferencia del mostrador. En una mesa el nombre
                es cortesía, no logística: el pedido se toma igual sin él, y robarle el foco al
                operador en hora pico convertiría un dato amable en un estorbo. El Sprint 111 ya
                nos costó un reclamo por poner aquí una pregunta que no aplicaba.
              */}
              <div className="mt-3">
                <label htmlFor="nombreMesa" className="mb-1 block text-xs font-medium text-slate-700">
                  🙋 ¿A nombre de quién? <span className="text-slate-600">(opcional)</span>
                </label>
                <input
                  id="nombreMesa"
                  className="input w-full text-sm"
                  value={cliente?.nombre ?? ''}
                  onChange={(e) => setCliente({ nombre: e.target.value })}
                  placeholder="Doña Mary, los del bautizo…"
                  maxLength={60}
                  disabled={enviando}
                />
                <p className="mt-1 text-xs text-slate-600">Sale en su ticket.</p>
              </div>
          </div>
          )}

          <div className="flex flex-1 overflow-hidden">
            <Carrito
              tipo={tipo}
              paraRecoger={paraRecoger}
              clienteNombre={cliente?.nombre}
              onConfirmar={handleConfirmarCarrito}
              onCancelar={cancelar}
              enviando={enviando}
              modo={modoAgregar ? 'agregar' : 'crear'}
              problemas={problemasCarrito}
              /*
                Sprint 140 — A qué cuenta se suman, con el nombre del canal correcto. Hasta hoy
                decía "de la mesa" siempre, porque agregar sólo existía para mesas.
              */
              destinoAgregar={
                pedidoAgregar?.mesaId
                  ? 'Se suman a la cuenta de la mesa'
                  : pedidoAgregar?.tipo === 'delivery'
                  ? 'Se suman al pedido a domicilio'
                  : pedidoAgregar?.clienteNombre
                  ? `Se suman al pedido de ${pedidoAgregar.clienteNombre}`
                  : 'Se suman a la cuenta'
              }
            />
          </div>
        </div>
      </div>

      {/*
        Sprint 98 — Los pasos de captura del domicilio, como ventanas sobre el POS.

        Antes eran pantallas: cada paso sustituía a la anterior y el menú no existía hasta el
        tercero. Ahora el pedido se ve siempre debajo —lo que se leyó del chat, el carrito, el
        total— y lo que cambia es sólo la pregunta de arriba. Es el mismo patrón con el que este
        sistema captura todo lo demás desde el Sprint 7.
      */}
      {fase === 'cliente' && (
        <QuienYADondeForm
          inicial={cliente}
          entregaInicial={entrega ?? envioInicial}
          /*
            Sprint 98 — El pedido por WhatsApp empieza AQUÍ, no en el tablero.

            El menú y el precio se los pasa el POS, que es quien sabe el stock; lo que se entienda
            del chat entra al carrito por `agregarDesdeChat`, el mismo camino que un clic en el
            menú —con su tope de stock y su aviso de agotado—.
          */
          productos={productosConStockDisponible}
          monedaSimbolo={monedaSimbolo}
          onLineasDelChat={agregarDesdeChat}
          onSiguiente={handleQuienYADondeListo}
          onAtras={() => (tieneTipoPredefinido ? navigate(rutaSalida) : setFase('tipo'))}
        />
      )}

      {fase === 'envio' && (
        <CostoEnvioForm
          inicial={envio ?? envioInicial}
          entrega={entrega}
          totalPedido={totalProductos}
          monedaSimbolo={monedaSimbolo}
          nombreNegocio={negocio?.nombre}
          enviando={enviando}
          /* Sprint 137 — Para poder decir si ya ha pedido antes. Dato, no candado. */
          telefonoCliente={cliente?.telefono}
          onSiguiente={handleCostoListo}
          onDejarEsperando={handleDejarEsperando}
          onAtras={() => setFase('menu')}
        />
      )}

      {/*
        El aviso de error del envío va por encima de la ventana (z-[60]): si se quedara debajo,
        el operador vería el botón sin responder y ningún motivo.
      */}
      {errorEnvio && fase === 'envio' && (
        <div className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 rounded-lg bg-red-600 px-4 py-2 text-white shadow-lg">
          ⚠️ {errorEnvio}
        </div>
      )}

      <ModificadorPaqueteProductos
        abierto={!!paqueteEditando}
        paquete={paqueteEditando}
        onClose={() => setPaqueteEditando(null)}
        onConfirmar={handleConfirmarPaquete}
      />

      {pedidoCreadoFolio !== null && pedidoCreadoId !== null && (
        <PostCreacionModal
          folio={pedidoCreadoFolio}
          tipo={tipo}
          paraRecoger={paraRecoger}
          onCobrarAhora={handleCobrarAhora}
          onNuevoPedido={handleNuevoPedido}
          onCerrar={() => {
            setPedidoCreadoId(null);
            setPedidoCreadoFolio(null);
            // Sprint 111 — El mostrador vuelve a su cola, que es donde va a surtir y cobrar.
            navigate(paraRecoger ? '/mostrador' : '/dashboard');
          }}
        />
      )}

      {/* Sprint 28 — confirmación tras agregar productos a un pedido en curso */}
      {agregadoOk && (
        <PostAgregadoModal
          folio={agregadoOk.folio}
          cantidad={agregadoOk.cantidad}
          onAgregarMas={() => setAgregadoOk(null)}
          onListo={() => {
            setAgregadoOk(null);
            navigate(-1);
          }}
        />
      )}

      <CobroModal
        abierto={cobroId !== null}
        pedidoId={cobroId}
        onClose={() => {
          setCobroId(null);
          handleNuevoPedido();
        }}
        onCobrado={handleCobrado}
      />

      <TicketPreviewModal
        abierto={previewId !== null}
        pedidoId={previewId}
        pdfPath={previewPdfPath}
        onClose={handleCerrarPreview}
      />
    </div>
  );
}

function PostCreacionModal({
  folio,
  tipo,
  paraRecoger,
  onCobrarAhora,
  onNuevoPedido,
  onCerrar,
}: {
  folio: number;
  tipo: 'comer_aqui' | 'delivery';
  /** Sprint 111 — El mostrador tiene su propio orden: primero se surte, después se cobra. */
  paraRecoger?: boolean;
  onCobrarAhora: () => void;
  onNuevoPedido: () => void;
  onCerrar: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex flex-col items-center gap-3 px-6 py-6 text-center">
          <CheckCircle2 className="h-14 w-14 text-green-500" />
          {/*
            Sprint 111 — En mostrador nada "se envía a cocina": se surte.

            Es DT-102 aplicado al vocabulario. Aquí todo se vende ya cocinado, así que lo que sigue
            es pesar y armar la bolsa — decirle "enviado a cocina" a quien va a surtirlo ella misma
            le enseña un modelo del negocio que no es el suyo.
          */}
          <h2 className="text-xl font-bold">
            {paraRecoger ? 'Pedido tomado' : 'Pedido enviado a cocina'}
          </h2>
          <div className="text-3xl font-mono font-bold text-primary">
            #{String(folio).padStart(5, '0')}
          </div>
          <p className="text-sm text-slate-500">
            {/*
              Sprint 112 — El nombre de la columna, el de verdad.

              Decía «Falta cobrar», que fue el nombre que tuvo el tablero a media tarde del
              Sprint 111 y que esa misma noche pasó a ser «Surtiendo». El texto se quedó atrás:
              mandaba a buscar una columna que no existe en la pantalla. A quien apenas está
              aprendiendo el sistema, eso no le suena a etiqueta vieja — le suena a que se perdió
              el pedido.
            */}
            {paraRecoger
              ? 'Súrtelo y cóbralo cuando esté listo. Lo tienes en «Surtiendo».'
              : tipo === 'delivery'
              ? '¿Cobrar ahora antes de que salga el repartidor?'
              : 'Puedes cobrar ahora o después de entregar la comida.'}
          </p>
        </div>
        <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
          {/*
            Sprint 111 — En mostrador, la salida principal es el tablero y no el cobro.

            *"Primero se surte el pedido"* (la dueña, 2026-08-26). El botón grande tiene que ser el
            siguiente paso real; dejar "Cobrar ahora" arriba invitaría a cobrar antes de saber si
            hay todo lo que pidió. Sigue disponible abajo para cuando la bolsa ya está lista.
          */}
          {paraRecoger ? (
            <>
              <button onClick={onCerrar} className="btn-primary btn-lg">
                <ShoppingBag className="h-5 w-5" />
                Ir a surtirlo
              </button>
              <button onClick={onNuevoPedido} className="btn-secondary">
                Tomar otro pedido
              </button>
              <button onClick={onCobrarAhora} className="btn-ghost text-xs">
                Ya está listo: cobrar ahora
              </button>
            </>
          ) : (
            <>
              <button onClick={onCobrarAhora} className="btn-primary btn-lg">
                <DollarSign className="h-5 w-5" />
                Cobrar ahora
              </button>
              <button onClick={onNuevoPedido} className="btn-secondary">
                <ShoppingBag className="h-4 w-4" />
                Tomar otro pedido
              </button>
              <button onClick={onCerrar} className="btn-ghost text-xs">
                Cobrar después (volver al inicio)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Sprint 28 — Confirmación tras anexar productos a un pedido de mesa en curso.
 */
function PostAgregadoModal({
  folio,
  cantidad,
  onAgregarMas,
  onListo,
}: {
  folio: number;
  cantidad: number;
  onAgregarMas: () => void;
  onListo: () => void;
}) {
  const unico = cantidad === 1;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex flex-col items-center gap-3 px-6 py-6 text-center">
          <CheckCircle2 className="h-14 w-14 text-green-500" />
          <h2 className="text-xl font-bold">
            {unico ? 'Producto agregado' : 'Productos agregados'}
          </h2>
          <p className="text-sm text-slate-600">
            Se {unico ? 'sumó' : 'sumaron'} <strong>{cantidad}</strong>{' '}
            {unico ? 'producto' : 'productos'} a la cuenta del pedido
          </p>
          <div className="text-3xl font-mono font-bold text-primary">
            #{String(folio).padStart(5, '0')}
          </div>
          <p className="text-sm text-slate-500">La cocina ya recibió la nueva tanda.</p>
        </div>
        <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <button onClick={onListo} className="btn-primary btn-lg">
            <CheckCircle2 className="h-5 w-5" />
            Listo
          </button>
          <button onClick={onAgregarMas} className="btn-secondary">
            <ShoppingBag className="h-4 w-4" />
            Agregar más productos
          </button>
        </div>
      </div>
    </div>
  );
}
